import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Users, ChevronLeft, RefreshCw, Loader2, AlertCircle, Check, Search, X,
  FileSpreadsheet, Printer, ArrowRightLeft, Undo2,
} from "lucide-react";
import {
  useProbationerReviewQueueQuery,
  useAssignProbationerReviewerMutation,
  useReassignProbationerReviewerMutation,
  useUnassignProbationerReviewerMutation,
} from "../api/probationerApplicationApi";
import {
  categoryOf,
  exportReviewedPdf,
  exportReviewedSpreadsheet,
  groupByCategory,
  splitReviewed,
  verdictOf,
} from "../utils/exportReviewedAssignments";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";

/**
 * Assign ENTRY applications in a diet to the application review committee.
 *
 * The entry-application counterpart of the upgrade review cycle screen. Auto-
 * Assign scatters everything round-robin in one shot; this is the queue an
 * admin works when they want to place particular applications with particular
 * reviewers, and see what is stuck and why.
 */

const ROLE_LABEL: Record<string, string> = {
  chairman: "Chairman",
  secretary: "Secretary",
  member: "Member",
};

/** Which slice of the assigned table is on screen — and in the export. */
type DecisionFilter = "all" | "awaiting" | "approved" | "rejected" | "other";

const FILTER_LABEL: Record<DecisionFilter, string> = {
  all: "All",
  awaiting: "Awaiting assessment",
  approved: "Approved",
  rejected: "Rejected",
  other: "Other decisions",
};

const DECISION_STYLE: Record<string, string> = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  returned: "bg-amber-50 text-amber-700 border-amber-200",
};

/** A filter chip carrying its own count. */
function Chip({
  label,
  count,
  on,
  onClick,
}: {
  label: string;
  count: number;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold transition ${
        on
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
      }`}
    >
      {label}
      <span
        className={`tabular-nums text-[10px] font-black px-1.5 py-0.5 rounded-full ${
          on ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * What can be done to an assignment the reviewer has not reported on.
 *
 * Both actions in one place because they are the same decision taken two
 * ways — this person is not going to assess it, so either name who will or
 * put it back for the secretariat to decide. Shown together rather than
 * hiding the pool behind the move dialog, since "I do not know who yet" is
 * the commoner answer when a reviewer has gone quiet.
 *
 * Labelled, not icon-only: the old single X said "remove this reviewer",
 * which reads as deleting something.
 */
function RowActions({
  row,
  busy,
  onMove,
  onPool,
}: {
  row: any;
  busy: boolean;
  onMove: (row: any) => void;
  onPool: (row: any) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => onMove(row)}
        disabled={busy}
        title="Give this application to a different committee member"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-100 text-[11px] font-bold text-gray-500 hover:text-blue-600 hover:border-blue-200 disabled:opacity-50"
      >
        <ArrowRightLeft className="w-3.5 h-3.5" />
        Reassign
      </button>
      <button
        type="button"
        onClick={() => onPool(row)}
        disabled={busy}
        title="Take it off this reviewer and put it back in the pool"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-100 text-[11px] font-bold text-gray-500 hover:text-amber-600 hover:border-amber-200 disabled:opacity-50"
      >
        <Undo2 className="w-3.5 h-3.5" />
        To pool
      </button>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: any; tone?: string }) {
  return (
    <div className="rounded-2xl border border-gray-100 p-4">
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-black tabular-nums ${tone ?? "text-gray-900"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

/**
 * Where an application is read from an admin screen.
 *
 * The ADMIN review page, not /probationer-applications/:id — that one is the
 * applicant's own view of their application and is gated to member and
 * probationer roles, so an admin following it lands on a permission wall
 * rather than the review they were trying to open.
 */
const applicationHref = (id: string | number) => `/probationer-applications/admin/${id}`;

function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const rows = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return null;
  const max = rows[0][1] || 1;

  return (
    <div>
      <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{title}</h3>
      <div className="space-y-1.5">
        {rows.map(([label, count]) => (
          <div key={label} className="flex items-center gap-3">
            <div className="w-40 shrink-0 truncate text-xs font-semibold text-gray-600">{label}</div>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-400"
                style={{ width: `${Math.max(4, (count / max) * 100)}%` }}
              />
            </div>
            <div className="w-8 text-right text-xs font-bold tabular-nums text-gray-700">{count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminProbationerReviewAssignmentPage() {
  /* `categoryId` is absent on the diet-wide route and present on the
     per-category one. Everything below reads the same either way — the server
     narrows the lists, so the screen does not have to know which it is beyond
     saying so in the header. */
  const { dietId = "", categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* Where an application opened FROM, carried along so its Back button can
     return here rather than to the category list. Named, so the button can
     say where it goes instead of a bare "Back". */
  const cameFrom = {
    from: { pathname: location.pathname + location.search, label: "Review Assignment" },
  };

  /* The whole row opens the application, not just the name. The name link
     stays — it is what makes the row obviously openable, and it is what a
     middle-click or "open in new tab" needs. */
  const openApplication = (id: string | number) =>
    navigate(applicationHref(id), { state: cameFrom });

  const { data, isLoading, isFetching, refetch } = useProbationerReviewQueueQuery(
    { dietId, categoryId },
    { skip: !dietId },
  );

  const [assign, { isLoading: assigning }] = useAssignProbationerReviewerMutation();
  const [reassign, { isLoading: reassigning }] = useReassignProbationerReviewerMutation();
  const [unassign, { isLoading: unassigning }] = useUnassignProbationerReviewerMutation();

  const diet = data?.diet;
  const committee = data?.committee;
  /* The category the screen is scoped to, named by the server. Falls back to
     the name the chooser handed over in route state, so the header is right
     on the first paint rather than after the fetch lands. */
  const scopedCategory = categoryId
    ? data?.category?.name ?? (location.state as any)?.categoryName ?? null
    : null;
  const panel: any[] = useMemo(() => data?.members ?? [], [data]);
  const assigned: any[] = useMemo(() => data?.assigned ?? [], [data]);
  const pool: any[] = useMemo(() => data?.unassigned ?? [], [data]);
  const notReady: any[] = useMemo(() => data?.not_ready ?? [], [data]);
  const analysis = data?.analysis;

  const [picked, setPicked] = useState<string[]>([]);
  /* The panel member whose work is being looked at. The whole row set is
     already on this page — the dialog is a lens over it, not another fetch. */
  const [panelMember, setPanelMember] = useState<any | null>(null);
  /* The assignment being moved to someone else. Held as the row rather than
     an id so the dialog can name the applicant and who has it now. */
  const [movingRow, setMovingRow] = useState<any | null>(null);
  const [reviewer, setReviewer] = useState("");
  const [q, setQ] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return pool;
    return pool.filter((a) =>
      `${a.name ?? ""} ${a.membership_no ?? ""} ${a.category ?? ""} ${a.chapter ?? ""}`
        .toLowerCase()
        .includes(s),
    );
  }, [pool, q]);

  const allPicked = filtered.length > 0 && filtered.every((a) => picked.includes(a.id));

  /* The assigned table, filed the way the export prints it: approved, then
     rejected. Computed here rather than inside the export so the filter chips
     can carry their counts and the card can switch itself off when there is
     nothing to take. */
  const reviewed = useMemo(() => splitReviewed(assigned), [assigned]);
  const awaiting = useMemo(() => assigned.filter((a) => verdictOf(a) === null), [assigned]);

  /* Which decisions the table is showing. A diet's assigned list runs to
     hundreds of rows; "what did the committee turn down" is a question you
     answer by looking at the rejected ones alone, not by reading a Decision
     column down the page. */
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>("all");

  const byDecision = useMemo(() => {
    if (decisionFilter === "all") return assigned;
    if (decisionFilter === "awaiting") return awaiting;
    if (decisionFilter === "approved") return reviewed.approved;
    if (decisionFilter === "rejected") return reviewed.rejected;
    return reviewed.other;
  }, [assigned, awaiting, reviewed, decisionFilter]);

  /* And by category — student, probationer, technician. The two filters stack:
     "the rejected technicians" is a question somebody asks of a diet, and it
     is two chips rather than a column read down the page.

     The category counts are taken from the DECISION-filtered rows, so they
     answer "how do the rejected break down" rather than repeating the diet's
     totals under every decision. */
  const [categoryFilter, setCategoryFilter] = useState("all");

  const categoryChips = useMemo(() => {
    const groups = groupByCategory(byDecision);

    /* A category can stay selected while the decision filter changes under it
       — e.g. Student is picked, then Rejected, and no student was rejected.
       Keep its chip on screen at 0 rather than leaving an empty table with
       nothing highlighted to explain it. */
    if (
      categoryFilter !== "all" &&
      !groups.some((g) => g.category.toLowerCase() === categoryFilter.toLowerCase())
    ) {
      groups.push({ category: categoryFilter, rows: [] });
    }

    return groups;
  }, [byDecision, categoryFilter]);

  const assignedShown = useMemo(
    () =>
      categoryFilter === "all"
        ? byDecision
        : byDecision.filter(
            (a) => categoryOf(a).toLowerCase() === categoryFilter.toLowerCase(),
          ),
    [byDecision, categoryFilter],
  );

  const filtering = decisionFilter !== "all" || categoryFilter !== "all";

  const clearFilters = () => {
    setDecisionFilter("all");
    setCategoryFilter("all");
  };

  /* The export takes WHAT IS ON SCREEN, exactly as the category listing's
     exports do — a listing taken while a filter is up and silently containing
     the rows the filter hid is the kind of thing that gets signed. Filtered to
     Approved, the PDF is the approved roll with an empty Rejected section
     saying so. */
  const shownReviewed = useMemo(() => splitReviewed(assignedShown), [assignedShown]);

  const FILTER_CHIPS: { key: DecisionFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: assigned.length },
    { key: "awaiting", label: "Awaiting", count: awaiting.length },
    { key: "approved", label: "Approved", count: reviewed.approved.length },
    { key: "rejected", label: "Rejected", count: reviewed.rejected.length },
    // Only when something landed there — see verdictOf().
    ...(reviewed.other.length
      ? [{ key: "other" as DecisionFilter, label: "Other", count: reviewed.other.length }]
      : []),
  ];

  const exportMeta = useMemo(
    () => ({
      diet: diet?.title ?? undefined,
      committee: committee?.name ?? undefined,
      // Named in the export's header, so a filtered listing says on its face
      // that it is one.
      filter: decisionFilter === "all" ? undefined : FILTER_LABEL[decisionFilter],
      // The scoped category when the screen is scoped — the chips are hidden
      // then, so categoryFilter stays "all" and would print an export headed
      // as though it covered the whole diet.
      category:
        scopedCategory ?? (categoryFilter === "all" ? undefined : categoryFilter),
    }),
    [diet?.title, committee?.name, decisionFilter, categoryFilter, scopedCategory],
  );

  const run = async (fn: () => Promise<any>, fallback: string) => {
    setBanner(null);
    try {
      const res: any = await fn();
      setBanner({ ok: true, text: res?.message ?? fallback });
      return true;
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || "That did not work. Please try again." });
      return false;
    }
  };

  /* ── Moving a pending assignment ───────────────────────────────────────
     Two ways out of a reviewer's hands while they have not yet reported:
     hand it to somebody else, or put it back in the pool for the secretariat
     to place again. The server refuses both once a verdict is recorded, so
     these are offered only on rows with no decision. */
  const moveToPool = (row: any) =>
    run(
      () => unassign({ dietId, applicationId: row.id }).unwrap(),
      "Back in the pool.",
    );

  const moveToMember = async (memberId: string) => {
    if (!movingRow) return;
    const done = await run(
      () =>
        reassign({
          dietId,
          applicationId: movingRow.id,
          member_id: memberId,
        }).unwrap(),
      "Moved.",
    );
    if (done) setMovingRow(null);
  };

  const busyMoving = reassigning || unassigning;

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <PageGate perm={PERMS.application_diet.allCategoriesAccess}>
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          {/* Back where the press came from: the category chooser when this is
              one category's queue, the diet's category list otherwise. */}
          <Link
            to={
              categoryId
                ? `/probationer-applications/admin/submissions/diets/${dietId}/assignments/categories`
                : `/probationer-applications/admin/submissions/diets/${dietId}/categories`
            }
            className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
          >
            <ChevronLeft className="w-3.5 h-3.5" />{" "}
            {categoryId ? "Pick another category" : "Diet categories"}
          </Link>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                  Review Assignment
                  {scopedCategory ? (
                    <span className="text-blue-600"> · {scopedCategory}</span>
                  ) : null}
                </h1>
                <p className="text-sm font-semibold text-gray-500">
                  {[diet?.title, committee?.name].filter(Boolean).join(" · ") || "Entry applications"}
                </p>
                {/* Said plainly, because every count on the page is narrowed by
                    it — a panel member's "3 pending" here means three in THIS
                    category, not three in the diet. */}
                {scopedCategory ? (
                  <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mt-0.5">
                    This category only
                  </p>
                ) : null}
              </div>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/* Without a committee there is nobody to assign to — say where to fix it. */}
          {!committee ? (
            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
              <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
              <div className="text-sm font-semibold text-rose-800">
                {data?.message ?? "No application review committee was found."}{" "}
                <Link to="/executive-sets" className="underline">
                  Go to Executive Sets
                </Link>
              </div>
            </div>
          ) : null}
        </div>

        {banner && (
          <div
            className={`px-4 py-3 rounded-2xl text-sm font-semibold border ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                : "bg-rose-50 border-rose-100 text-rose-800"
            }`}
          >
            {banner.text}
          </div>
        )}

        {/* ── Analysis ────────────────────────────────────────────────── */}
        {analysis ? (
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <h2 className="text-lg font-black text-gray-900">Analysis</h2>
            <p className="text-sm text-gray-500 mb-4">
              Where this diet's entry review stands.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Stat label="Submitted" value={analysis.applications?.submitted} />
              <Stat label="Assigned" value={analysis.applications?.assigned} />
              <Stat label="Reviewed" value={analysis.applications?.reviewed} tone="text-emerald-600" />
              <Stat
                label="Awaiting decision"
                value={analysis.applications?.awaiting_decision}
                tone={analysis.applications?.awaiting_decision > 0 ? "text-amber-600" : undefined}
              />
              <Stat label="Waiting in pool" value={analysis.applications?.waiting_in_pool} />
              <Stat
                label="Held back"
                value={analysis.applications?.held_back}
                tone={analysis.applications?.held_back > 0 ? "text-rose-600" : undefined}
              />
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <Breakdown title="By decision" data={analysis.by_decision} />
              <Breakdown title="By category" data={analysis.by_category} />
              <Breakdown title="By chapter" data={analysis.by_chapter} />
            </div>

            {analysis.workload?.length ? (
              <div className="mt-6">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                  Reviewer workload in this diet
                </h3>
                <div className="border rounded-2xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                        <th className="px-4 py-2">Reviewer</th>
                        <th className="px-4 py-2 text-right">Assigned</th>
                        <th className="px-4 py-2 text-right">Reviewed</th>
                        <th className="px-4 py-2 text-right">Pending</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {analysis.workload.map((w: any) => (
                        <tr key={w.name}>
                          <td className="px-4 py-2 font-semibold text-gray-800">
                            {w.name}
                            {w.is_former ? (
                              <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                former
                              </span>
                            ) : null}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">{w.assigned_count}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-emerald-700">
                            {w.decided_count}
                          </td>
                          <td
                            className={`px-4 py-2 text-right tabular-nums font-bold ${
                              w.pending_count > 0 ? "text-amber-600" : "text-gray-400"
                            }`}
                          >
                            {w.pending_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ── Panel ───────────────────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-gray-900">Panel</h2>
          <p className="text-sm text-gray-500 mb-4">
            Everyone on the application review committee. The counts are what each is carrying in
            this diet.
          </p>

          {panel.length === 0 ? (
            <p className="text-sm text-gray-400">Nobody is on the committee yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {panel.map((m: any) => (
                // A card is the natural thing to click to ask "what is this
                // person carrying?" — a button rather than a div so the
                // keyboard reaches it too.
                <button
                  key={m.member_id}
                  type="button"
                  onClick={() => setPanelMember(m)}
                  title={`See everything assigned to ${m.name} in this diet`}
                  className={`text-left rounded-2xl border p-4 transition hover:border-blue-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                    m.is_former ? "border-dashed border-gray-200 bg-gray-50/60" : "border-gray-100"
                  }`}
                >
                  <div className="font-bold text-gray-900 truncate">{m.name}</div>
                  <div className="text-[11px] font-semibold text-gray-400 mt-0.5">
                    {[ROLE_LABEL[m.committee_role] ?? m.committee_role, m.membership_no]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {m.is_former ? (
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">
                      No longer on the committee
                    </div>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                        m.pending_count > 0
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-gray-50 text-gray-400 border-gray-200"
                      }`}
                    >
                      {m.pending_count ?? 0} pending
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border bg-gray-50 text-gray-500 border-gray-200">
                      {m.assigned_count ?? 0} assigned
                    </span>
                  </div>
                  <div className="mt-2 text-[11px] font-semibold text-blue-600">
                    See their applications →
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Assign from the pool ────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-black text-gray-900">Ready to assign</h2>
              <p className="text-sm text-gray-500">
                Submitted in this diet, with the application fee settled, the chapter endorsement in
                and both referees approved — and nobody reviewing them yet. Assigning one hands it
                to the Membership Committee.
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, category or chapter…"
                className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>

          {pool.length === 0 ? (
            <p className="mt-6 text-sm text-gray-400">
              Nothing is ready to hand out — every eligible application already has a reviewer.
            </p>
          ) : (
            <>
              <div className="mt-4 border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                      <th className="px-4 py-2 w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={allPicked}
                          onChange={() => setPicked(allPicked ? [] : filtered.map((a) => a.id))}
                        />
                      </th>
                      <th className="px-4 py-2">Applicant</th>
                      <th className="px-4 py-2">Category</th>
                      <th className="px-4 py-2">Chapter</th>
                      <th className="px-4 py-2 text-right">Waiting</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map((a) => (
                      <tr
                        key={a.id}
                        onClick={() => openApplication(a.id)}
                        title="Open this application"
                        className="hover:bg-gray-50/60 cursor-pointer"
                      >
                        {/* The checkbox is for picking, not opening — the row
                            click must not fire underneath it, or selecting
                            somebody would navigate away from the selection. */}
                        <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={picked.includes(a.id)}
                            onChange={() =>
                              setPicked((p) =>
                                p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id],
                              )
                            }
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <Link
                            to={applicationHref(a.id)}
                            state={cameFrom}
                            className="font-semibold text-gray-900 hover:text-blue-600"
                          >
                            {a.name || "—"}
                          </Link>
                          <div className="text-[11px] text-gray-400">
                            {a.membership_no || a.email}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{a.category || "—"}</td>
                        <td className="px-4 py-2.5 text-gray-500">{a.chapter || "—"}</td>
                        <td
                          className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                            (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                          }`}
                        >
                          {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                <select
                  value={reviewer}
                  onChange={(e) => setReviewer(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
                >
                  <option value="">Assign to…</option>
                  {/* Former members keep their existing work but take no new. */}
                  {panel
                    .filter((m: any) => !m.is_former)
                    .map((m: any) => (
                      <option key={m.member_id} value={m.member_id}>
                        {m.name} ({m.pending_count ?? 0} pending)
                      </option>
                    ))}
                </select>
                <button
                  onClick={async () => {
                    const done = await run(
                      () => assign({ dietId, member_id: reviewer, application_ids: picked }).unwrap(),
                      "Assigned.",
                    );
                    if (done) setPicked([]);
                  }}
                  disabled={assigning || !reviewer || picked.length === 0}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {assigning ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  Assign {picked.length || ""}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Already assigned ────────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-black text-gray-900">Assigned applications</h2>
              <p className="text-sm text-gray-500">
                Who is reviewing what, and what they have decided so far.
              </p>
              {/* Said before the buttons are pressed: the export is of the
                  DECIDED rows on screen, so this is what will come out of it.
                  A pending row in a signed listing of decisions is worse than
                  no listing. */}
              <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                Export: {shownReviewed.total} decision
                {shownReviewed.total === 1 ? "" : "s"} ·{" "}
                {shownReviewed.approved.length} approved · {shownReviewed.rejected.length} rejected
                {shownReviewed.other.length ? ` · ${shownReviewed.other.length} other` : ""}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => exportReviewedSpreadsheet(assignedShown, exportMeta)}
                disabled={shownReviewed.total === 0}
                title={
                  shownReviewed.total === 0
                    ? "Nothing on screen has been reviewed yet"
                    : "Download the reviewed applications on screen as a spreadsheet — approved and rejected in separate sections"
                }
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>
              <button
                onClick={() => exportReviewedPdf(assignedShown, exportMeta)}
                disabled={shownReviewed.total === 0}
                title={
                  shownReviewed.total === 0
                    ? "Nothing on screen has been reviewed yet"
                    : "Print the reviewed applications on screen, or save them as a PDF — approved and rejected in separate sections"
                }
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
              >
                <Printer className="w-4 h-4" />
                PDF
              </button>
            </div>
          </div>

          {/* ── Filters ─────────────────────────────────────────────────
              Chips rather than selects: the counts are half the answer, and a
              select hides them until it is opened. Two rows, labelled — one
              undifferentiated row of chips would read as one filter. */}
          {assigned.length > 0 ? (
            <div className="mb-4 space-y-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-16 shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Decision
                </span>
                {FILTER_CHIPS.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    count={chip.count}
                    on={decisionFilter === chip.key}
                    onClick={() => setDecisionFilter(chip.key)}
                  />
                ))}
              </div>

              {/* The category chips are for the diet-wide screen. Scoped to one
                  category the row would be a single chip restating the
                  heading, and an "All" chip next to it reading as though it
                  could widen the page, which it cannot. */}
              <div className="flex flex-wrap items-center gap-2">
                {!categoryId ? (
                  <>
                    <span className="w-16 shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400">
                      Category
                    </span>
                    <Chip
                      label="All"
                      count={byDecision.length}
                      on={categoryFilter === "all"}
                      onClick={() => setCategoryFilter("all")}
                    />
                    {categoryChips.map((group) => (
                      <Chip
                        key={group.category}
                        label={group.category}
                        count={group.rows.length}
                        on={categoryFilter.toLowerCase() === group.category.toLowerCase()}
                        onClick={() => setCategoryFilter(group.category)}
                      />
                    ))}
                  </>
                ) : null}
                {filtering ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="ml-1 inline-flex items-center gap-1 text-[11px] font-bold text-gray-400 hover:text-gray-700"
                  >
                    <X className="w-3 h-3" /> Clear
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {assigned.length === 0 ? (
            <p className="text-sm text-gray-400">Nothing has been assigned yet.</p>
          ) : assignedShown.length === 0 ? (
            <p className="text-sm text-gray-400">
              No{" "}
              {categoryFilter === "all" ? "" : `${categoryFilter.toLowerCase()} `}
              application in this diet is{" "}
              {decisionFilter === "all"
                ? "assigned"
                : FILTER_LABEL[decisionFilter].toLowerCase()}
              .{" "}
              <button
                type="button"
                onClick={clearFilters}
                className="font-bold text-blue-600 hover:underline"
              >
                Clear the filters
              </button>
            </p>
          ) : (
            <div className="border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Reviewer</th>
                    <th className="px-4 py-2">Decision</th>
                    <th className="px-4 py-2 text-right">Waiting</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {assignedShown.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => openApplication(a.id)}
                      title="Open this application"
                      className="hover:bg-gray-50/60 cursor-pointer"
                    >
                      <td className="px-4 py-2.5">
                        <Link
                          to={applicationHref(a.id)}
                          state={cameFrom}
                          className="font-semibold text-gray-900 hover:text-blue-600"
                        >
                          {a.name || "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{a.membership_no || a.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{a.category || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-700 font-semibold">
                        {a.reviewer || "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        {a.decision ? (
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                              DECISION_STYLE[a.decision] ??
                              "bg-gray-50 text-gray-600 border-gray-200"
                            }`}
                          >
                            {a.decision}
                          </span>
                        ) : (
                          <span className="text-[11px] font-semibold text-amber-600">pending</span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                          (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                        }`}
                      >
                        {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                      </td>
                      {/* Moving a reviewer is not "open the row" — kept out of
                          the row's click so a misfire cannot navigate instead
                          of moving, or move instead of navigating. */}
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {/* A recorded decision is part of the record — only an
                            untouched assignment can be moved. */}
                        {!a.decision ? (
                          <RowActions
                            row={a}
                            busy={busyMoving}
                            onMove={setMovingRow}
                            onPool={moveToPool}
                          />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Held back ───────────────────────────────────────────────── */}
        {notReady.length > 0 ? (
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <h2 className="text-lg font-black text-gray-900">Held back</h2>
            <p className="text-sm text-gray-500 mb-4">
              Submitted in this diet but not yet assignable. Each one names what is missing —
              clearing it moves the application into the pool above.
            </p>

            <div className="border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Waiting on</th>
                    <th className="px-4 py-2 text-right">Waiting</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {notReady.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => openApplication(a.id)}
                      title="Open this application"
                      className="hover:bg-gray-50/60 cursor-pointer"
                    >
                      <td className="px-4 py-2.5">
                        <Link
                          to={applicationHref(a.id)}
                          state={cameFrom}
                          className="font-semibold text-gray-900 hover:text-blue-600"
                        >
                          {a.name || "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{a.membership_no || a.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{a.category || "—"}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1.5">
                          {(a.blockers ?? []).map((b: string) => (
                            <span
                              key={b}
                              className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-rose-50 text-rose-700 border-rose-200"
                            >
                              {b}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                          (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                        }`}
                      >
                        {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {movingRow ? (
          <ReassignDialog
            row={movingRow}
            panel={panel}
            busy={busyMoving}
            onPick={moveToMember}
            onPool={async () => {
              const done = await moveToPool(movingRow);
              if (done) setMovingRow(null);
            }}
            onClose={() => setMovingRow(null)}
          />
        ) : null}

        {panelMember ? (
          <PanelMemberDialog
            member={panelMember}
            rows={assigned.filter(
              (a) => String(a.reviewer_id ?? "") === String(panelMember.member_id),
            )}
            linkState={cameFrom}
            busy={busyMoving}
            onMove={(row) => {
              // Close the lens first: two stacked dialogs with the same
              // backdrop makes one press close both.
              setPanelMember(null);
              setMovingRow(row);
            }}
            onPool={moveToPool}
            onOpen={(id) => {
              // Close first: leaving a dialog mounted over the page you just
              // navigated to is how a back-button press lands somewhere
              // nobody asked for.
              setPanelMember(null);
              openApplication(id);
            }}
            onClose={() => setPanelMember(null)}
          />
        ) : null}
      </div>
    </PageGate>
  );
}

/**
 * Who to move a pending assignment to.
 *
 * The panel, with each member's current load, because that is the question
 * being answered — work is moved off somebody who has stalled, and moving it
 * onto the next most loaded person is how it stalls again. The person holding
 * it now is listed too, disabled, so the list does not silently omit them and
 * leave the reader wondering whether they scrolled past.
 *
 * "Back to the pool" sits in the footer rather than as a third icon in the
 * row: having opened this dialog the admin may well find nobody free, and
 * closing it to press something else is a dead end.
 */
function ReassignDialog({
  row,
  panel,
  busy,
  onPick,
  onPool,
  onClose,
}: {
  row: any;
  panel: any[];
  busy: boolean;
  onPick: (memberId: string) => void;
  onPool: () => void;
  onClose: () => void;
}) {
  // Former members keep what they hold but take no new work — the same rule
  // the pool's "Assign to…" select applies.
  const candidates = panel.filter((m) => !m.is_former);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-900/60"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 truncate">
              Reassign {row.name || "this application"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {row.reviewer ? `With ${row.reviewer} now` : "No reviewer recorded"}
              {row.category ? ` · ${row.category}` : ""}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500 p-2">
              Nobody on the committee can take new work.
            </p>
          ) : (
            <div className="space-y-1.5">
              {candidates.map((m) => {
                const holdsIt = String(m.member_id) === String(row.reviewer_id ?? "");

                return (
                  <button
                    key={m.member_id}
                    type="button"
                    disabled={busy || holdsIt}
                    onClick={() => onPick(m.member_id)}
                    className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 rounded-xl border border-gray-100 hover:border-blue-300 hover:bg-blue-50/40 disabled:opacity-50 disabled:hover:border-gray-100 disabled:hover:bg-transparent transition"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{m.name}</div>
                      <div className="text-[11px] font-semibold text-gray-400">
                        {[ROLE_LABEL[m.committee_role] ?? m.committee_role, m.membership_no]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    {holdsIt ? (
                      <span className="shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400">
                        Has it now
                      </span>
                    ) : (
                      <span
                        className={`shrink-0 inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                          m.pending_count > 0
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-gray-50 text-gray-400 border-gray-200"
                        }`}
                      >
                        {m.pending_count ?? 0} pending
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={onPool}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-200 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
            Put back in the pool
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Everything one panel member is carrying in this diet.
 *
 * Split into what they have ASSESSED and what is still WAITING on them,
 * because those are two different conversations: the first is a record of
 * work done, the second is the chase list. A single mixed table makes the
 * admin do that sorting by eye every time they open it.
 *
 * Reads the rows the page already has rather than fetching again — the
 * assignment table on the page and this dialog are then incapable of
 * disagreeing about who holds what.
 */
function PanelMemberDialog({
  member,
  rows,
  onOpen,
  linkState,
  busy,
  onMove,
  onPool,
  onClose,
}: {
  member: any;
  rows: any[];
  onOpen: (id: string | number) => void;
  linkState: any;
  busy: boolean;
  onMove: (row: any) => void;
  onPool: (row: any) => void;
  onClose: () => void;
}) {
  const assessed = rows.filter((r) => !!r.decision);
  const awaiting = rows.filter((r) => !r.decision);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-900/60"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 truncate">{member.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {[ROLE_LABEL[member.committee_role] ?? member.committee_role, member.membership_no]
                .filter(Boolean)
                .join(" · ")}
              {" · "}
              {rows.length} in this diet
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nothing has been assigned to {member.name} in this diet yet.
            </p>
          ) : (
            <>
              {/* The chase list is where moving work belongs — this is the
                  screen an admin is on when they decide somebody has had long
                  enough. The assessed table takes no actions: a recorded
                  verdict cannot be moved. */}
              <MemberRows
                title={`Awaiting their assessment (${awaiting.length})`}
                rows={awaiting}
                empty="They have assessed everything handed to them."
                onOpen={onOpen}
                linkState={linkState}
                busy={busy}
                onMove={onMove}
                onPool={onPool}
              />
              <MemberRows
                title={`Assessed (${assessed.length})`}
                rows={assessed}
                empty="They have not assessed any of these yet."
                onOpen={onOpen}

                linkState={linkState}
              />
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberRows({
  title,
  rows,
  empty,
  onOpen,
  linkState,
  busy,
  onMove,
  onPool,
}: {
  title: string;
  rows: any[];
  empty: string;
  onOpen: (id: string | number) => void;
  /** Carried onto the link so the application's Back button returns here. */
  linkState: any;
  /** Omitted on the assessed table, which takes no actions. */
  busy?: boolean;
  onMove?: (row: any) => void;
  onPool?: (row: any) => void;
}) {
  const actionable = !!onMove && !!onPool;

  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
        {title}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">{empty}</p>
      ) : (
        <div className="border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                <th className="px-3 py-2">Applicant</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Decision</th>
                <th className="px-3 py-2 text-right">Waiting</th>
                {actionable ? <th className="px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => onOpen(a.id)}
                  title="Open this application"
                  className="hover:bg-gray-50/60 cursor-pointer"
                >
                  <td className="px-3 py-2">
                    <Link
                      to={applicationHref(a.id)}
                      state={linkState}
                      className="font-semibold text-gray-900 hover:text-blue-600"
                    >
                      {a.name || "—"}
                    </Link>
                    <div className="text-[11px] text-gray-400">{a.membership_no || a.email}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{a.category || "—"}</td>
                  <td className="px-3 py-2">
                    {a.decision ? (
                      <>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                            DECISION_STYLE[a.decision] ?? "bg-gray-50 text-gray-600 border-gray-200"
                          }`}
                        >
                          {a.decision}
                        </span>
                        {/* When, not just what — the date is how an admin
                            tells a decision made this sitting from one
                            carried in from an earlier one. */}
                        {a.decided_at ? (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {String(a.decided_at).slice(0, 10)}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-[11px] font-semibold text-amber-600">pending</span>
                    )}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums font-bold ${
                      (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                    }`}
                  >
                    {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                  </td>
                  {actionable ? (
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <RowActions
                        row={a}
                        busy={!!busy}
                        onMove={onMove!}
                        onPool={onPool!}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
